import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  CaseEndpoint,
  CreateRelationshipRequest,
  QuestionTemplate,
} from '../../../types/case-container'
import {
  BidirectionalToggle,
  ConnectionTypeSelect,
  DirectionPreview,
  ErrorBanner,
  FormActions,
  LabelInput,
  NotesField,
  OpenQuestionSection,
  StatusButtons,
  TargetEndpointSelect,
} from './create-relationship-modal/create-relationship-modal.parts'
import { useCreateRelationshipModal } from './create-relationship-modal/use-create-relationship-modal'

interface CreateRelationshipModalProps {
  sourceEndpoint: CaseEndpoint
  endpoints: CaseEndpoint[]
  templates: QuestionTemplate[]
  onClose: () => void
  onCreate: (data: CreateRelationshipRequest) => void
  creating: boolean
  error: string | null
}

export function CreateRelationshipModal(props: CreateRelationshipModalProps): ReactElement {
  const { sourceEndpoint, endpoints, templates, onClose, onCreate, creating, error } = props
  const { t } = useTranslation('cases')
  const state = useCreateRelationshipModal({ sourceEndpoint, endpoints, onCreate })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Create Relationship
        </h3>
        <DirectionPreview sourceEndpoint={sourceEndpoint} selectedTarget={state.selectedTarget} />
        <form onSubmit={state.handleSubmit} className="space-y-4">
          {error && <ErrorBanner error={error} />}
          <TargetEndpointSelect
            availableTargets={state.availableTargets}
            formData={state.formData}
            setFormData={state.setFormData}
          />
          <ConnectionTypeSelect formData={state.formData} setFormData={state.setFormData} />
          <StatusButtons
            formData={state.formData}
            isOpenQuestion={state.isOpenQuestion}
            handleStatusChange={state.handleStatusChange}
          />
          <LabelInput formData={state.formData} setFormData={state.setFormData} t={t} />
          {state.isOpenQuestion ? (
            <OpenQuestionSection
              templates={templates}
              formData={state.formData}
              setFormData={state.setFormData}
              t={t}
            />
          ) : (
            <NotesField formData={state.formData} setFormData={state.setFormData} t={t} />
          )}
          <BidirectionalToggle
            bidirectional={state.bidirectional}
            setBidirectional={state.setBidirectional}
          />
          <FormActions
            onClose={onClose}
            creating={creating}
            formData={state.formData}
            availableTargetsCount={state.availableTargets.length}
          />
        </form>
      </div>
    </div>
  )
}
